import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { ScoreDrilldownDialog, StartupCardSkeleton } from '../components/features/Cards';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { startupApi } from '../lib/api';
import {
  cn,
  formatConfidencePercent,
  formatCurrency,
  formatDateWithPrecision,
  formatRoundLabel,
  getScoreColor,
  readSessionJson,
  writeSessionJson,
} from '../lib/utils';
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Filter,
  Linkedin,
  MapPin,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  TrendingUp,
  Users,
  Building2,
} from 'lucide-react';

const buildDiscoverCacheKey = (query, filters, page, limit) => (
  `agent-vc.discover.v3:${JSON.stringify({
    query: query || '',
    sector: filters.sector || '',
    stage: filters.stage || '',
    source_vc: filters.source_vc || '',
    sortBy: filters.sortBy || 'conviction_score',
    page,
    limit,
  })}`
);

const DiscoverPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [startups, setStartups] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    sector: '',
    stage: '',
    source_vc: '',
    sortBy: 'conviction_score'
  });
  const [page, setPage] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newStartup, setNewStartup] = useState({ name: '', website: '', sector: '', stage: 'early', description: '' });
  const [creating, setCreating] = useState(false);
  const [viewMode, setViewMode] = useState('cards'); // cards, table
  const [scoreDialogOpen, setScoreDialogOpen] = useState(false);
  const [scoreDrilldown, setScoreDrilldown] = useState(null);
  const [scoreDrilldownLoading, setScoreDrilldownLoading] = useState(false);
  const [scoreDrilldownError, setScoreDrilldownError] = useState(null);
  const [selectedStartup, setSelectedStartup] = useState(null);

  const LIMIT = 50;

  const fetchStartups = async (resetPage = false) => {
    setLoading(true);
    const currentPage = resetPage ? 0 : page;
    if (resetPage) setPage(0);
    const cacheKey = buildDiscoverCacheKey(searchQuery, filters, currentPage, LIMIT);
    const cachedPayload = readSessionJson(cacheKey, null);
    
    try {
      const params = {
        query: searchQuery || undefined,
        sector: filters.sector || undefined,
        stage: filters.stage || undefined,
        source_vc: filters.source_vc || undefined,
        sort_by: filters.sortBy,
        limit: LIMIT,
        skip: currentPage * LIMIT
      };
      const response = await startupApi.list(params);
      setStartups(response.data.startups || []);
      setTotal(response.data.total || 0);
      writeSessionJson(cacheKey, response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch startups:', err);
      if (cachedPayload) {
        setStartups(cachedPayload.startups || []);
        setTotal(cachedPayload.total || 0);
        setError('Live search is warming up. Showing the freshest cached startup research available.');
      } else {
        setStartups([]);
        setTotal(0);
        setError('Could not load startup research right now. Try refresh again in a moment.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStartups(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (page > 0) fetchStartups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchStartups(true);
  };

  const openScoreDrilldown = async (startup) => {
    setSelectedStartup(startup);
    setScoreDialogOpen(true);
    setScoreDrilldown(null);
    setScoreDrilldownError(null);
    setScoreDrilldownLoading(true);

    try {
      const response = await startupApi.getScoreDrilldown(startup.startup_id);
      setScoreDrilldown(response.data);
    } catch (err) {
      console.error('Failed to fetch score drilldown:', err);
      setScoreDrilldownError('The persisted scoring trace could not be loaded.');
    } finally {
      setScoreDrilldownLoading(false);
    }
  };

  const handleCreateStartup = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await startupApi.create(newStartup);
      setStartups([response.data, ...startups]);
      setShowAddModal(false);
      setNewStartup({ name: '', website: '', sector: '', stage: 'early', description: '' });
    } catch (err) {
      console.error('Failed to create startup:', err);
    } finally {
      setCreating(false);
    }
  };

  const sectors = ['AI/ML', 'Fintech', 'SaaS', 'Healthcare', 'Infrastructure', 'Consumer', 'Enterprise', 'Crypto/Web3', 'Climate', 'Developer Tools'];
  const stages = ['early', 'growth', 'late'];
  const vcSources = ['Y Combinator', 'Sequoia Capital', 'Andreessen Horowitz', 'Khosla Ventures', 'Greylock Partners', 'Benchmark', 'Founders Fund', 'Lightspeed Venture Partners', 'Accel', 'NEA', 'Index Ventures', 'General Catalyst', 'Bessemer Venture Partners', 'Tiger Global', 'Coatue Management', 'Insight Partners', 'Kleiner Perkins', 'Spark Capital', 'Ribbit Capital', 'First Round Capital', 'Thrive Capital', 'Felicis Ventures', 'Redpoint Ventures'];
  const sortOptions = [
    { value: 'conviction_score', label: 'Conviction Score', icon: Sparkles },
    { value: 'investor_respect', label: 'Investor Respect', icon: Users },
    { value: 'team_confidence', label: 'Team Confidence', icon: Users },
    { value: 'sector_momentum', label: 'Sector Momentum', icon: TrendingUp },
    { value: 'recent', label: 'Recently Added', icon: RefreshCw }
  ];

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <MainLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-light font-['Outfit'] text-zinc-50">Discover</h1>
            <p className="text-zinc-400 font-['Manrope']">
              <span className="text-zinc-50 font-medium">{total.toLocaleString()}</span> startups sourced from top VCs
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => fetchStartups(true)}
              variant="outline"
              data-testid="refresh-startups-btn"
              className="border-zinc-800 text-zinc-400 hover:text-zinc-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
              <DialogTrigger asChild>
                <Button 
                  data-testid="add-startup-btn"
                  className="bg-white text-zinc-900 hover:bg-zinc-200"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Startup
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800">
                <DialogHeader>
                  <DialogTitle className="text-zinc-50 font-['Outfit']">Add New Startup</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateStartup} className="space-y-4 mt-4">
                  <div>
                    <label className="text-sm text-zinc-400 font-['Manrope'] mb-1 block">Company Name</label>
                    <Input
                      value={newStartup.name}
                      onChange={(e) => setNewStartup({ ...newStartup, name: e.target.value })}
                      placeholder="Acme Inc"
                      required
                      data-testid="input-startup-name"
                      className="bg-zinc-800 border-zinc-700 text-zinc-50"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400 font-['Manrope'] mb-1 block">Website</label>
                    <Input
                      value={newStartup.website}
                      onChange={(e) => setNewStartup({ ...newStartup, website: e.target.value })}
                      placeholder="https://acme.com"
                      data-testid="input-startup-website"
                      className="bg-zinc-800 border-zinc-700 text-zinc-50"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-zinc-400 font-['Manrope'] mb-1 block">Sector</label>
                      <Select 
                        value={newStartup.sector} 
                        onValueChange={(v) => setNewStartup({ ...newStartup, sector: v })}
                      >
                        <SelectTrigger data-testid="select-sector" className="bg-zinc-800 border-zinc-700 text-zinc-50">
                          <SelectValue placeholder="Select sector" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          {sectors.map((s) => (
                            <SelectItem key={s} value={s} className="text-zinc-50 hover:bg-zinc-700">{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm text-zinc-400 font-['Manrope'] mb-1 block">Stage</label>
                      <Select 
                        value={newStartup.stage} 
                        onValueChange={(v) => setNewStartup({ ...newStartup, stage: v })}
                      >
                        <SelectTrigger data-testid="select-stage" className="bg-zinc-800 border-zinc-700 text-zinc-50">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          {stages.map((s) => (
                            <SelectItem key={s} value={s} className="text-zinc-50 hover:bg-zinc-700 capitalize">{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400 font-['Manrope'] mb-1 block">Description</label>
                    <textarea
                      value={newStartup.description}
                      onChange={(e) => setNewStartup({ ...newStartup, description: e.target.value })}
                      placeholder="What does this company do?"
                      rows={3}
                      data-testid="input-startup-description"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-zinc-50 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-zinc-600"
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-2">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowAddModal(false)}
                      className="border-zinc-700 text-zinc-400"
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit"
                      disabled={creating || !newStartup.name}
                      data-testid="submit-startup-btn"
                      className="bg-white text-zinc-900 hover:bg-zinc-200"
                    >
                      {creating ? 'Creating...' : 'Add Startup'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {error && (
          <div className="mb-6 flex items-start gap-3 rounded-md border border-amber-400/20 bg-amber-400/10 px-4 py-3">
            <AlertCircle className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-100">{error}</p>
          </div>
        )}

        {/* Search & Filters */}
        <div className="flex flex-col gap-4 mb-6">
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search startups by name, sector, description, or VC..."
                data-testid="search-input"
                className="pl-10 bg-zinc-900 border-zinc-800 text-zinc-50 placeholder:text-zinc-500"
              />
            </div>
            <Button type="submit" variant="outline" className="border-zinc-800 text-zinc-400 hover:text-zinc-50">
              <Search className="w-4 h-4" />
            </Button>
          </form>
          
          <div className="flex items-center gap-3 flex-wrap">
            <Select 
              value={filters.sector} 
              onValueChange={(v) => setFilters({ ...filters, sector: v === 'all' ? '' : v })}
            >
              <SelectTrigger data-testid="filter-sector" className="w-36 bg-zinc-900 border-zinc-800 text-zinc-50">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Sector" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700 max-h-80">
                <SelectItem value="all" className="text-zinc-50">All Sectors</SelectItem>
                {sectors.map((s) => (
                  <SelectItem key={s} value={s} className="text-zinc-50 hover:bg-zinc-700">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select 
              value={filters.stage} 
              onValueChange={(v) => setFilters({ ...filters, stage: v === 'all' ? '' : v })}
            >
              <SelectTrigger data-testid="filter-stage" className="w-32 bg-zinc-900 border-zinc-800 text-zinc-50">
                <SelectValue placeholder="Stage" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="all" className="text-zinc-50">All Stages</SelectItem>
                {stages.map((s) => (
                  <SelectItem key={s} value={s} className="text-zinc-50 hover:bg-zinc-700 capitalize">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select 
              value={filters.source_vc} 
              onValueChange={(v) => setFilters({ ...filters, source_vc: v === 'all' ? '' : v })}
            >
              <SelectTrigger data-testid="filter-vc" className="w-48 bg-zinc-900 border-zinc-800 text-zinc-50">
                <Building2 className="w-4 h-4 mr-2" />
                <SelectValue placeholder="VC Source" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700 max-h-80">
                <SelectItem value="all" className="text-zinc-50">All VCs</SelectItem>
                {vcSources.map((vc) => (
                  <SelectItem key={vc} value={vc} className="text-zinc-50 hover:bg-zinc-700">{vc}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex-1" />

            <Select 
              value={filters.sortBy} 
              onValueChange={(v) => setFilters({ ...filters, sortBy: v })}
            >
              <SelectTrigger data-testid="filter-sort" className="w-48 bg-zinc-900 border-zinc-800 text-zinc-50">
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                {sortOptions.map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <SelectItem key={opt.value} value={opt.value} className="text-zinc-50 hover:bg-zinc-700">
                      <span className="flex items-center gap-2">
                        <Icon className="w-4 h-4" />
                        {opt.label}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results */}
        <div className="grid grid-cols-2 gap-4">
          {loading ? (
            [...Array(8)].map((_, i) => <StartupCardSkeleton key={i} />)
          ) : startups.length > 0 ? (
            startups.map((startup) => (
              <StartupCardWithScores
                key={startup.startup_id}
                startup={startup}
                onClick={() => navigate(`/startups/${startup.startup_id}`)}
                onScoreClick={startup.score_drilldown_available ? openScoreDrilldown : undefined}
              />
            ))
          ) : (
            <div className="col-span-2 bg-zinc-900 border border-zinc-800 rounded-md p-12 text-center">
              <p className="text-zinc-400 font-['Manrope'] mb-4">
                {searchQuery ? 'No startups match your search' : 'No startups yet'}
              </p>
              <Button
                onClick={() => setShowAddModal(true)}
                data-testid="empty-add-startup"
                className="bg-white text-zinc-900 hover:bg-zinc-200"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Startup
              </Button>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-zinc-800">
            <p className="text-zinc-500 text-sm font-mono">
              Showing {page * LIMIT + 1}-{Math.min((page + 1) * LIMIT, total)} of {total.toLocaleString()}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="border-zinc-800 text-zinc-400 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-zinc-400 text-sm font-mono px-3">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="border-zinc-800 text-zinc-400 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        <ScoreDrilldownDialog
          open={scoreDialogOpen}
          onOpenChange={setScoreDialogOpen}
          startupName={selectedStartup?.name}
          drilldown={scoreDrilldown}
          loading={scoreDrilldownLoading}
          error={scoreDrilldownError}
          onRetry={() => selectedStartup && openScoreDrilldown(selectedStartup)}
        />
      </div>
    </MainLayout>
  );
};

// Enhanced startup card with detailed scores
const StartupCardWithScores = ({ startup, onClick, onScoreClick }) => {
  const convictionScore = startup.conviction_score?.total_score || startup.conviction_score || 0;
  const investorScore = startup.investor_respect_score || startup.conviction_score?.investor_respect_score || 0;
  const teamScore = startup.team_confidence_score || startup.conviction_score?.team_confidence_score || 0;
  const sectorScore = startup.sector_momentum_score || startup.conviction_score?.sector_momentum_score || 0;
  const founderPreview = Array.isArray(startup.founder_preview) ? startup.founder_preview : [];
  const founderNames = founderPreview.map((founder) => founder?.name).filter(Boolean).join(' • ');
  const latestFundingRound = startup.latest_funding_round || null;
  const latestFundingDate = latestFundingRound?.date
    ? formatDateWithPrecision(latestFundingRound.date, latestFundingRound.date_precision)
    : null;
  const trustSummary = startup.trust_summary || null;
  
  return (
    <div
      onClick={onClick}
      data-testid={`startup-card-${startup.startup_id}`}
      className="bg-zinc-900 border border-zinc-800 rounded-md p-4 cursor-pointer transition-all duration-200 hover:border-zinc-600 hover:bg-zinc-900/80"
    >
      <div className="flex items-start gap-3">
        {/* Logo */}
        <div className="w-12 h-12 bg-zinc-800 rounded-md flex items-center justify-center flex-shrink-0 overflow-hidden">
          {startup.logo_url ? (
            <img src={startup.logo_url} alt={startup.name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-zinc-500 font-mono text-lg">{startup.name?.charAt(0)}</span>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between gap-2 mb-1">
            <h3 className="text-zinc-50 font-medium font-['Outfit'] truncate text-lg">{startup.name}</h3>
            {convictionScore > 0 && (
              onScoreClick ? (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onScoreClick(startup);
                  }}
                  data-testid={`discover-score-open-${startup.startup_id}`}
                  className={cn("flex items-center gap-1 font-mono text-sm px-2 py-0.5 rounded-full transition-colors hover:bg-zinc-800",
                    convictionScore >= 80 ? 'bg-emerald-400/10 text-emerald-400' :
                    convictionScore >= 60 ? 'bg-blue-400/10 text-blue-400' :
                    convictionScore >= 40 ? 'bg-amber-400/10 text-amber-400' : 'bg-zinc-800 text-zinc-400'
                  )}
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>{Math.round(convictionScore)}</span>
                </button>
              ) : (
                <div className={cn("flex items-center gap-1 font-mono text-sm px-2 py-0.5 rounded-full", 
                  convictionScore >= 80 ? 'bg-emerald-400/10 text-emerald-400' :
                  convictionScore >= 60 ? 'bg-blue-400/10 text-blue-400' :
                  convictionScore >= 40 ? 'bg-amber-400/10 text-amber-400' : 'bg-zinc-800 text-zinc-400'
                )}>
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>{Math.round(convictionScore)}</span>
                </div>
              )
            )}
          </div>
          
          {/* Description */}
          {startup.description && (
            <p className="text-zinc-400 text-sm font-['Manrope'] mb-2 line-clamp-2">
              {startup.description}
            </p>
          )}
          
          {/* Tags */}
          <div className="flex items-center gap-2 flex-wrap mb-3">
            {startup.stage && (
              <span className={cn("px-2 py-0.5 text-xs font-mono rounded-full border",
                startup.stage === 'early' ? 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20' :
                startup.stage === 'growth' ? 'text-blue-400 bg-blue-400/10 border-blue-400/20' :
                startup.stage === 'late' ? 'text-amber-400 bg-amber-400/10 border-amber-400/20' :
                'text-zinc-400 bg-zinc-800 border-zinc-700'
              )}>
                {startup.stage}
              </span>
            )}
            {startup.sector && (
              <span className="px-2 py-0.5 text-xs font-mono text-zinc-400 bg-zinc-800 rounded-full">
                {startup.sector}
              </span>
            )}
            {startup.source_vcs?.length > 1 ? (
              <span className="px-2 py-0.5 text-xs font-mono text-violet-400 bg-violet-400/10 rounded-full" title={startup.source_vcs.join(', ')}>
                {startup.source_vcs.length} VCs
              </span>
            ) : startup.source_vc ? (
              <span className="px-2 py-0.5 text-xs font-mono text-blue-400 bg-blue-400/10 rounded-full">
                {startup.source_vc}
              </span>
            ) : null}
            {startup.total_funding > 0 && (
              <span className="px-2 py-0.5 text-xs font-mono text-zinc-300 bg-zinc-800 rounded-full">
                {formatCurrency(startup.total_funding)}
              </span>
            )}
            {latestFundingRound?.round_type && (
              <span className="px-2 py-0.5 text-xs font-mono text-emerald-300 bg-emerald-400/10 rounded-full border border-emerald-400/20">
                {formatRoundLabel(latestFundingRound.round_type)}
                {latestFundingDate ? ` • ${latestFundingDate}` : ''}
              </span>
            )}
            {trustSummary?.confidence_percent && (
              <span className="px-2 py-0.5 text-xs font-mono text-zinc-300 bg-zinc-800 rounded-full border border-zinc-700">
                <ShieldCheck className="w-3 h-3 inline mr-1" />
                {trustSummary.confidence_percent}%
              </span>
            )}
            {startup.city && (
              <span className="flex items-center gap-0.5 text-zinc-500 text-[11px]">
                <MapPin className="w-3 h-3" />{startup.city}
              </span>
            )}
          </div>

          {(founderNames || trustSummary?.signals?.length > 0) && (
            <div className="flex items-center gap-3 flex-wrap mb-3 text-xs">
              {founderNames && (
                <span className="flex items-center gap-1 text-zinc-300">
                  <Users className="w-3.5 h-3.5 text-zinc-500" />
                  {founderNames}
                </span>
              )}
              {founderPreview.some((founder) => founder?.linkedin_url) && (
                <span className="flex items-center gap-1 text-zinc-500">
                  <Linkedin className="w-3.5 h-3.5" />
                  LinkedIn
                </span>
              )}
              {trustSummary?.signals?.slice(0, 2).map((signal) => (
                <span key={signal.key || signal.label} className="text-zinc-500 font-mono">
                  {signal.label}
                </span>
              ))}
            </div>
          )}
          
          {/* Score breakdown */}
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1">
              <Users className="w-3.5 h-3.5 text-zinc-500" />
              <span className="text-zinc-500">Investor:</span>
              <span className={cn("font-mono", getScoreColor(investorScore))}>{Math.round(investorScore)}</span>
            </div>
            <div className="flex items-center gap-1">
              <Users className="w-3.5 h-3.5 text-zinc-500" />
              <span className="text-zinc-500">Team:</span>
              <span className={cn("font-mono", getScoreColor(teamScore))}>{Math.round(teamScore)}</span>
            </div>
            <div className="flex items-center gap-1">
              <TrendingUp className="w-3.5 h-3.5 text-zinc-500" />
              <span className="text-zinc-500">Momentum:</span>
              <span className={cn("font-mono", getScoreColor(sectorScore))}>{Math.round(sectorScore)}</span>
            </div>
            {trustSummary?.confidence && (
              <div className="flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5 text-zinc-500" />
                <span className="text-zinc-500">Trust:</span>
                <span className="font-mono text-zinc-300">{formatConfidencePercent(trustSummary.confidence)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscoverPage;
